import {
    Option,
    TableComponentProps,
} from '../../models/table-models';
import { useTranslation } from 'react-i18next';
import { useGetWarehouses } from '../../utils/warehouses-hooks';
import { useGetUsers } from '../../utils/users-hooks';
import { useAsyncResource } from 'use-async-resource';
import React, { Suspense, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select, { OnChangeValue } from 'react-select';
import { states } from '../../utils/Enums/Passports';
import { CustomStepWizardNav } from '../../components/CustomStepWizardNav';
import StepWizard from 'react-step-wizard';
import dateFormat from "dateformat";
import { InventoryReferences } from "./InventoryReferences";
import { NothingSelectedComponent } from "../../components/NothingSelectedComponent";
import { Spinner } from "react-bootstrap";
import { PassportType } from "../../models/warehouse-models";
import { TasksReferences } from "./TasksReferences";

export const References = ({ filter, pagination, setFilter }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
    const [selectedDistributors, setSelectedDistributors] = useState<string[]>([]);
    const [selectedState, setSelectedState] = useState<string[]>([]);
    const [selectedType, setSelectedType] = useState<string>();
    const [showAll, setShowAll] = useState<boolean>(false);

    const [getWarehouses] = useAsyncResource(useGetWarehouses, {});
    const [getDistributors] = useAsyncResource(useGetUsers, undefined);

    const warehousesOptions = getWarehouses().items;
    const users = getDistributors().items;
    const emptyOption = { name: '', value: '' };

    // All user/warehouse IDs for "all" selection
    const allUserIds: string[] = (users?.map(u => u.id?.toString()).filter(Boolean) as string[]) ?? [];
    const allWarehouseIds: string[] = (warehousesOptions?.map(w => w.id?.toString()).filter(Boolean) as string[]) ?? [];

    const onChange = (value: OnChangeValue<Option, true>) => {
        let ids = value.map(({ value }) => value);
        setSelectedWarehouses(ids);
    };

    const onChangeDistributor = (value: OnChangeValue<Option, true>) => {
        let ids = value.map(({ value }) => value);
        setSelectedDistributors(ids);

        if (filter && filter.after == undefined) {
            setFilter(() => ({
                ...filter,
                after: dateFormat(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-mm-dd')
            }));
        }
    };

    const onChangeState = (value: OnChangeValue<Option, true>) => {
        let ids = value.map(({ value }) => value);
        setSelectedState(ids);
    };

    const today = dateFormat(new Date(), 'yyyy-mm-dd');
    // if 'before' is empty, use today
    const effectiveBefore = filter?.before ?? today;

    // the filter object we'll actually pass to children / API
    const effectiveFilter = { ...filter, before: effectiveBefore };

    // dates are "ready" if we have at least a start date now
    const datesReady = !!effectiveFilter.after;

    // Default to all warehouses/users if none selected (lazy loading per tab handles performance)
    const effectiveWarehouses = selectedWarehouses.length > 0 ? selectedWarehouses : allWarehouseIds;
    const effectiveDistributorsForInventory = selectedDistributors.length > 0 ? selectedDistributors : allUserIds;

    return (
        <>
            <div className='w-100 d-flex align-self-start mb-5'>
                <Select
                    className='w-30'
                    placeholder={t('warehouses')}
                    options={
                        warehousesOptions
                            ? warehousesOptions.map((value) =>
                                value.warehouseName && value.id
                                    ? {
                                        name: value.warehouseName,
                                        value: value.id.toString(),
                                    }
                                    : emptyOption
                            )
                            : [emptyOption]
                    }
                    getOptionLabel={({ name }) => name}
                    getOptionValue={({ value }) => value}
                    maxMenuHeight={100}
                    isMulti
                    onChange={onChange}
                />
                <div className='w-100 d-flex justify-content-start'>
                    <Select
                        className='w-30'
                        placeholder={t('distributor')}
                        options={
                            users
                                ? users.map((value) =>
                                    value.username && value.id
                                        ? {
                                            name: value.username,
                                            value: value.id.toString(),
                                        }
                                        : emptyOption
                                )
                                : [emptyOption]
                        }
                        maxMenuHeight={100}
                        getOptionLabel={({ name }) => name}
                        getOptionValue={({ value }) => value}
                        isMulti
                        onChange={onChangeDistributor}
                    />
                </div>
                <Select
                    className='w-30'
                    placeholder={t('state')}
                    options={
                        states
                            ? states.map((value) =>
                                value.name && value.value
                                    ? {
                                        name: t(value.name),
                                        value: value.value,
                                    }
                                    : emptyOption
                            )
                            : [emptyOption]
                    }
                    getOptionLabel={({ name }) => name}
                    getOptionValue={({ value }) => value}
                    maxMenuHeight={100}
                    isMulti
                    onChange={onChangeState}
                />
                <Select
                    className='w-30'
                    placeholder={t('passportType')}
                    options={PassportType.map(
                        (value) =>
                            value
                                ? {
                                    name: t(value.toLowerCase()),
                                    value: value,
                                }
                                : emptyOption
                    )}
                    getOptionLabel={({ name }) => name}
                    getOptionValue={({ value }) => value}
                    onChange={(newValue) => {
                        if (newValue) {
                            setSelectedType(newValue.value);
                        }
                    }}
                />
            </div>

            <Form.Check
                type='checkbox'
                className='ms-3 d-flex align-items-center'
                label={t('showAllTables')}
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
            />
            <StepWizard
                className={'w-100'}
                nav={<CustomStepWizardNav steps={['tasks', 'inventoryReferences']} />}
                transitions={{
                    enterRight: '',
                    enterLeft: '',
                    exitRight: '',
                    exitLeft: '',
                }}
                initialStep={selectedDistributors.length > 0 ? 1 : 2}  //jump to Inventory when no distributors
            >
                {selectedDistributors.length > 0 ? (
                    <Suspense fallback={<Spinner animation='border' />}>
                        <TasksReferences filter={filter} users={users} userIds={selectedDistributors} />
                    </Suspense>
                ) : (
                    <NothingSelectedComponent nothingSelectedText={t('distributors')} />
                )}

                {/* Inventory – Lazy loads data per tab for performance */}
                {datesReady ? (
                    <Suspense fallback={<Spinner animation='border' />}>
                        <InventoryReferences
                            pagination={pagination}
                            filter={effectiveFilter}
                            selectedWarehouses={effectiveWarehouses}
                            selectedDistributors={effectiveDistributorsForInventory}
                            selectedState={selectedState}
                            selectedType={selectedType}
                            showAll={showAll}
                        />
                    </Suspense>
                ) : (
                    <NothingSelectedComponent
                        nothingSelectedText={t('pleaseSelectStartDate')}
                    />
                )}
            </StepWizard>
        </>
    );
};
